import Notice from '@/components/common/Notice';
import { Text } from '@/components/common/Typography';
import { toDollarsWithCents } from '@/util/currency';
import { FlexProps, Spinner } from '@chakra-ui/react';
import { useOnetimeTransactionSourceBalance } from '@common/react/goals';
import { isNumber } from '@common/util/types';
import { FlexCol } from '../common/Flex';
import InputField from '../common/InputField';
import OnetimeTransactionCardLayout from './TransactionCardLayout';

export interface TransactionAmountCardProps extends FlexProps {
    title: string;
    subtitle: string | JSX.Element;
    sourceId: string;
    amount: number;
    onChangeAmount: (amount: number) => void;
    onBack?: VoidFunction;
    onNext: VoidFunction;
    onClose: VoidFunction;
    pendingWithdrawalAmount: number | null;
    isCappedAtAvailableBalance: boolean;
    fullWithdrawalDescription?: string;
    contributionLimitStr?: string;
    minAmount?: number;
}

const TransactionAmountCard = ({
    title,
    subtitle,
    sourceId,
    amount,
    onChangeAmount,
    onBack,
    onNext,
    onClose,
    pendingWithdrawalAmount,
    isCappedAtAvailableBalance,
    fullWithdrawalDescription,
    contributionLimitStr,
    minAmount = 1,
    ...props
}: TransactionAmountCardProps) => {
    const { sourceBalance } = useOnetimeTransactionSourceBalance(sourceId);

    if (!isNumber(sourceBalance)) {
        // This should never happen
        console.warn('No source balance in TransactionAmountCard');
        onBack?.();
        return <></>;
    }

    if (!isNumber(pendingWithdrawalAmount)) {
        return <Spinner size="sm" color="gray.500" />;
    }

    const availableBalance = Math.max(
        sourceBalance - pendingWithdrawalAmount,
        0
    );

    return (
        <OnetimeTransactionCardLayout
            {...props}
            title={title}
            subtitle={subtitle}
            onBack={onBack}
            onNext={onNext}
            isNextDisabled={amount < minAmount}
            onClose={onClose}
        >
            <FlexCol grow={1} mt={6} mb={10} px={4}>
                <InputField
                    name="amount"
                    label=""
                    type="currency"
                    value={amount}
                    onChange={onChangeAmount}
                    variant="flushed"
                    inputProps={{
                        fontSize: '7xl',
                        h: 24,
                        textAlign: 'center',
                    }}
                    inputOptions={{
                        min: 0,
                        // Need to divide by 100 here because the input expects
                        // a number in dollars and the availableBalance is in cents
                        max: isCappedAtAvailableBalance
                            ? availableBalance / 100
                            : undefined,
                    }}
                    slider={{
                        min: 0,
                        max: Math.max(availableBalance, amount),
                        // TODO: move this into a helper
                        step:
                            availableBalance < 10_00
                                ? availableBalance
                                : availableBalance < 1000_00
                                  ? 10_00
                                  : 100_00,
                    }}
                />
                {amount < minAmount && (
                    <Notice mt={5}>
                        {`Minimum amount is ${toDollarsWithCents(minAmount)}`}
                    </Notice>
                )}
                {contributionLimitStr && (
                    <Notice mt={5} maxW={80}>
                        {contributionLimitStr}
                    </Notice>
                )}
                {pendingWithdrawalAmount > 0 && (
                    <Notice mt={5}>
                        <PendingWithdrawalAmountInfo
                            availableBalance={availableBalance}
                            pendingWithdrawalAmount={pendingWithdrawalAmount}
                            isCappedAtAvailableBalance={
                                isCappedAtAvailableBalance
                            }
                        />
                    </Notice>
                )}
                {fullWithdrawalDescription && (
                    <Notice mt={5}>
                        {`Withdrawing everything? ${fullWithdrawalDescription}`}
                    </Notice>
                )}
            </FlexCol>
        </OnetimeTransactionCardLayout>
    );
};

export default TransactionAmountCard;

// TODO: Keep this in sync with mobile/components/transaction/TransactionAmountView.tsx
const PendingWithdrawalAmountInfo = ({
    availableBalance,
    pendingWithdrawalAmount,
    isCappedAtAvailableBalance,
}: {
    availableBalance: number;
    pendingWithdrawalAmount: number;
    isCappedAtAvailableBalance: boolean;
}) => {
    if (isCappedAtAvailableBalance) {
        return (
            <>
                Your available balance is{' '}
                <Text color="textDark" fontSize="sm" fontWeight="semibold">
                    {toDollarsWithCents(availableBalance)}
                </Text>{' '}
                because of transfers that are in progress from this account.
            </>
        );
    }

    return (
        <>
            You already have{' '}
            <Text color="textDark" fontSize="sm" fontWeight="semibold">
                {toDollarsWithCents(pendingWithdrawalAmount)}
            </Text>{' '}
            of transfers that are in progress from this account.
        </>
    );
};
